/**
 * Validation Logger
 * Specialized logging utility for validation events in the chat system
 */

import { Question } from '@/types/theme';
import { ValidationResult } from '@/types/ai';

// Define validation event types
export enum ValidationEventType {
  VALIDATION_STARTED = 'VALIDATION_STARTED',
  VALIDATION_COMPLETED = 'VALIDATION_COMPLETED',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  VALIDATION_SKIPPED = 'VALIDATION_SKIPPED',
  VALIDATION_CONFIG_LOADED = 'VALIDATION_CONFIG_LOADED'
}

// Define validation log entry structure
export interface ValidationLogEntry {
  timestamp: string;
  eventType: ValidationEventType;
  questionId?: string;
  questionType?: string;
  questionText?: string;
  validationType?: string;
  userResponse?: string;
  validationResult?: Partial<ValidationResult>;
  attemptNumber?: number;
  maxAttempts?: number;
  error?: any;
  chatbotId?: string;
  sessionId?: string;
  metadata?: Record<string, any>;
}

// Create a custom event for validation logs
export const dispatchValidationLog = (entry: ValidationLogEntry): void => {
  // Add timestamp if not provided
  if (!entry.timestamp) {
    entry.timestamp = new Date().toISOString();
  }

  // Create and dispatch the event
  const event = new CustomEvent('chat-validation-log', {
    detail: entry
  });

  // Dispatch to document for Playwright to capture
  document.dispatchEvent(event);

  // Also log to console for development
  console.debug(`[VALIDATION] [${entry.eventType}]`, entry);
};

/**
 * Log validation configuration loaded
 */
export const logValidationConfigLoaded = (
  chatbotId: string,
  validationConfig: any,
  sessionId?: string
): void => {
  dispatchValidationLog({
    eventType: ValidationEventType.VALIDATION_CONFIG_LOADED,
    chatbotId,
    sessionId,
    metadata: {
      validationConfig
    }
  });
};

/**
 * Log validation started
 */
export const logValidationStarted = (
  question: Question,
  response: string,
  attemptNumber: number,
  maxAttempts: number,
  chatbotId?: string,
  sessionId?: string
): void => {
  dispatchValidationLog({
    eventType: ValidationEventType.VALIDATION_STARTED,
    questionId: question.id,
    questionType: question.type,
    questionText: question.text,
    validationType: question.validationType,
    userResponse: response,
    attemptNumber,
    maxAttempts,
    chatbotId,
    sessionId,
    metadata: {
      validationRules: question.validation_rules
    }
  });
};

/**
 * Log validation completed
 */
export const logValidationCompleted = (
  question: Question,
  response: string,
  result: ValidationResult,
  attemptNumber: number,
  maxAttempts: number,
  chatbotId?: string,
  sessionId?: string
): void => {
  dispatchValidationLog({
    eventType: ValidationEventType.VALIDATION_COMPLETED,
    questionId: question.id,
    questionType: question.type,
    questionText: question.text,
    validationType: question.validationType,
    userResponse: response,
    validationResult: result,
    attemptNumber,
    maxAttempts,
    chatbotId,
    sessionId
  });
};

/**
 * Log validation error
 */
export const logValidationError = (
  question: Question,
  response: string,
  error: any,
  attemptNumber: number,
  maxAttempts: number,
  chatbotId?: string,
  sessionId?: string
): void => {
  dispatchValidationLog({
    eventType: ValidationEventType.VALIDATION_ERROR,
    questionId: question.id,
    questionType: question.type,
    questionText: question.text,
    validationType: question.validationType,
    userResponse: response,
    error,
    attemptNumber,
    maxAttempts,
    chatbotId,
    sessionId
  });
};

/**
 * Log validation skipped
 */
export const logValidationSkipped = (
  question: Question,
  response: string,
  reason: string,
  chatbotId?: string,
  sessionId?: string
): void => {
  dispatchValidationLog({
    eventType: ValidationEventType.VALIDATION_SKIPPED,
    questionId: question.id,
    questionType: question.type,
    questionText: question.text,
    validationType: question.validationType,
    userResponse: response,
    chatbotId,
    sessionId,
    metadata: {
      reason
    }
  });
};
